import { createNewDocumentData, filterOrphanedNodes, filterValidNodes, organizationAndEnterpriseOwnerProperties, reKeyDocumentConfigs, resetChildUidsAndRootNode, } from "@buildwithflux/models";
/**
 * Builder for IDocumentData from constituent parts
 *
 * Helper builder with typed accessors that convert between DB data arrays and maps of entities
 */
export class DocumentDataBuilder {
    baseDocument;
    activeUsers;
    assets;
    commentThreads;
    configs;
    userUiStates;
    elements;
    nets;
    pcbLayoutNodes;
    pcbLayoutRuleSets;
    properties;
    roles;
    routes;
    routeVertices;
    invites;
    constructor(baseDocument) {
        this.baseDocument = {
            ...createNewDocumentData({
                uid: "",
                handle: "",
            }),
            ...baseDocument,
            // Special-case override for symbolLayoutStrategy because we want it to take
            // the unset/undefined value from baseDocument when DocumentDataBuilder is used
            // to load existing docs. When used to create new docs, the caller needs to
            // explicitly override that. TODO the defaults mechanism here makes this a bit
            // counterintuitive + error-prone, and hard to set a default in a single place.
            symbolLayoutStrategy: baseDocument.symbolLayoutStrategy,
        };
    }
    withOrganizationAndEnterpriseOwner(organizationOwner, enterpriseOwner) {
        this.baseDocument = {
            ...this.baseDocument,
            ...organizationAndEnterpriseOwnerProperties(organizationOwner, enterpriseOwner),
        };
        return this;
    }
    withActiveUsers(activeUserList) {
        const users = {};
        for (const user of activeUserList) {
            users[user.user_uid] = user;
        }
        this.activeUsers = users;
        return this;
    }
    withAssets(assetList) {
        const assets = {};
        for (const asset of assetList) {
            assets[asset.uid] = asset;
        }
        this.assets = assets;
        return this;
    }
    withCommentThreads(commentThreadList) {
        const commentThreads = {};
        for (const commentThread of commentThreadList) {
            commentThreads[commentThread.uid] = commentThread;
        }
        this.commentThreads = commentThreads;
        return this;
    }
    /**
     * @see FirestoreDocumentRepository.getConfigs also
     */
    withConfigs(configsList) {
        this.configs = reKeyDocumentConfigs(configsList);
        return this;
    }
    withElements(elementsList) {
        const elements = {};
        for (const element of elementsList) {
            elements[element.uid] = element;
        }
        this.elements = elements;
        return this;
    }
    withNets(netsList) {
        const nets = {};
        for (const net of netsList) {
            nets[net.uid] = net;
        }
        this.nets = nets;
        return this;
    }
    withPcbLayoutNodes(pcbLayoutNodeList) {
        const pcbLayoutNodes = {};
        for (const node of pcbLayoutNodeList) {
            pcbLayoutNodes[node.uid] = node;
        }
        // Recompute child uids for all nodes in case they are out of
        // sync. We decided against doing a one-time migration to ensure that
        // this critical derived data is always fresh and correct. There is a
        // small performance cost on load of the document.
        //
        // Re-add the root node in case it does not exist. It's data should be
        // immutable, so re-adding it should be idempotent. Until Jan 2024, no
        // actual root node existed, only the pcbLayoutRootNodeId. We decided
        // against doing a one-time migration because we are recomputing the
        // childUids anyway.
        //
        // Similarly, filter out any nodes that do not have a type or do not
        // have an existing parent.
        this.pcbLayoutNodes = resetChildUidsAndRootNode(filterOrphanedNodes(filterValidNodes(Object.values(pcbLayoutNodes))));
        return this;
    }
    withPcbLayoutRuleSets(pcbLayoutRuleSetList) {
        const pcbLayoutRuleSets = {};
        for (const ruleSet of pcbLayoutRuleSetList) {
            pcbLayoutRuleSets[ruleSet.uid] = ruleSet;
        }
        this.pcbLayoutRuleSets = pcbLayoutRuleSets;
        return this;
    }
    withProperties(propertyList) {
        const properties = {};
        for (const property of propertyList) {
            properties[property.uid] = property;
        }
        this.properties = properties;
        return this;
    }
    withRoles(userPermissionsList) {
        const userPermissions = {};
        for (const perms of userPermissionsList) {
            userPermissions[perms.user_uid] = perms;
        }
        this.roles = userPermissions;
        return this;
    }
    withRouteVertices(routeVertexList) {
        const routeVertices = {};
        for (const vertex of routeVertexList) {
            // NOTE: any vertex without id is considered invalid, and we should never load
            // it; otherwise it will at least break part rotations.
            // Ideally we want to figure out how does a vertex like that get created from the
            // first place, but we dont have a clear clue yet
            // See more detail in PR: https://github.com/buildwithflux/flux-app/pull/9780
            if (vertex.id) {
                routeVertices[vertex.id] = vertex;
            }
        }
        this.routeVertices = routeVertices;
        return this;
    }
    withRoutes(routeList) {
        const routes = {};
        for (const route of routeList) {
            routes[route.uid] = route;
        }
        this.routes = routes;
        return this;
    }
    withInvites(invitesList) {
        const invites = {};
        for (const invite of invitesList) {
            invites[invite.uid] = invite;
        }
        this.invites = invites;
        return this;
    }
    withUserUiStates(userUiStatesList) {
        const userUiStates = {};
        for (const uiState of userUiStatesList) {
            userUiStates[uiState.userUid] = uiState;
        }
        this.userUiStates = userUiStates;
        return this;
    }
    build() {
        return {
            ...this.baseDocument,
            active_users: this.activeUsers ?? {},
            assets: this.assets ?? {},
            comment_threads: this.commentThreads ?? {},
            configs: this.configs ?? {},
            userUiStates: this.userUiStates ?? {},
            elements: this.elements ?? {},
            nets: this.nets ?? {},
            pcbLayoutNodes: this.pcbLayoutNodes ?? {},
            pcbLayoutRuleSets: this.pcbLayoutRuleSets ?? {},
            properties: this.properties ?? {},
            roles: this.roles ?? {},
            routeVertices: this.routeVertices ?? {},
            routes: this.routes ?? {},
            invites: this.invites ?? {},
        };
    }
}
//# sourceMappingURL=DocumentDataBuilder.js.map